import React, { useEffect, useState } from "react";
import useAxios from "../../utils/useAxios";
import { FaSpinner, FaEye, FaUtensils, FaCalendarAlt } from "react-icons/fa";
import { hideModal, showModal } from "../../utils/modalUtility";

const customStyles = `
  .restaurant-card {
    border: none;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(149, 157, 165, 0.15);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    overflow: hidden;
  }
  .restaurant-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 32px rgba(149, 157, 165, 0.25);
  }
  .card-header-icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #e0e7ff, #ede9fe);
    color: #4f46e5;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    margin: 0 auto 15px auto;
  }
  .click-badge {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
    font-size: 2.5rem;
    display: inline-block;
    line-height: 1;
  }
  .btn-premium {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    color: white;
    border: none;
    border-radius: 12px;
    padding: 12px;
    font-weight: 600;
    transition: all 0.2s ease;
  }
  .btn-premium:hover {
    background: linear-gradient(135deg, #4338ca, #6d28d9);
    color: white;
    transform: scale(1.03);
    box-shadow: 0 8px 16px rgba(124, 58, 237, 0.3);
  }
  .modal-custom-header {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    color: white;
    border-bottom: none;
  }
  .list-group-custom .list-group-item {
    border: 1px solid #f1f5f9;
    margin-bottom: 10px;
    border-radius: 12px !important;
    background: #f8fafc;
    box-shadow: 0 2px 8px rgba(0,0,0,0.02);
    transition: all 0.2s ease;
  }
  .list-group-custom .list-group-item:hover {
    background: #ffffff;
    border-color: #e2e8f0;
    transform: translateX(4px);
  }
  .page-header-gradient {
    background: linear-gradient(135deg, #f8fafc, #f1f5f9);
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    border: 1px solid #e2e8f0;
  }
`;

const RestaurantClicks = () => {
  const [loading, setLoading] = useState(false);
  const [restaurantClicks, setRestaurantClicks] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sortOrder, setSortOrder] = useState("DESC");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const axiosAPI = useAxios();

  const fetchCities = async () => {
    try {
      const response = await axiosAPI.get(`/api/restaurant-clicks/cities`);
      setCities(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const fetchRestaurantClicks = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (selectedCity) params.append("city", selectedCity);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);

      const endpoint = `/api/restaurant-clicks/all?${params.toString()}`;

      const response = await axiosAPI.get(endpoint);
      const data = response?.data?.data || [];

      const grouped = data.reduce((acc, item) => {
        const name = item.name || "Unknown Restaurant";
        if (!acc[name]) {
          acc[name] = {
            id: item.id,
            name: name,
            city_name: item.city_name || "N/A",
            total_clicks: item.total_clicks || 0,
            views: []
          };
        } else {
          acc[name].total_clicks = Math.max(acc[name].total_clicks, item.total_clicks || 0);
        }
        if (item.view) {
          acc[name].views.push(item.view);
        }
        return acc;
      }, {});

      setRestaurantClicks(Object.values(grouped));
    } catch (error) {
      console.error("Error fetching restaurant clicks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    fetchRestaurantClicks();
  }, [selectedCity, startDate, endDate]);


  const handleViewClick = (restaurant) => {
    setSelectedRestaurant(restaurant);
    showModal("viewClicksModal");
  };

  const sortedRestaurantClicks = [...restaurantClicks].sort((a, b) => {
    if (sortOrder === "DESC") {
      return b.total_clicks - a.total_clicks;
    }
    return a.total_clicks - b.total_clicks;
  });


  return (
    <div className="container py-4">
      <style>{customStyles}</style>
      <div className="page-inner">

        <div className="page-header-gradient d-flex flex-wrap justify-content-between align-items-center shadow-sm gap-3">
          <div>
            <h3 className="fw-bold mb-1" style={{ color: "#1e293b" }}>Restaurant Insights</h3>
            <p className="text-muted mb-0">Track and analyze user engagement across all your restaurants.</p>
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="position-relative">
              <input
                type="text"
                className="form-control shadow-sm bg-white text-dark"
                placeholder="Search by city..."
                value={selectedCity}
                onChange={(e) => {
                  setSelectedCity(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                style={{ minWidth: "250px", borderRadius: "12px", border: "1px solid #e2e8f0" }}
              />
              {showSuggestions && cities.filter(c => c.toLowerCase().includes(selectedCity.toLowerCase())).length > 0 && (
                <ul
                  className="list-group position-absolute w-100 shadow"
                  style={{
                    top: "100%",
                    left: 0,
                    zIndex: 1000,
                    maxHeight: "250px",
                    overflowY: "auto",
                    borderRadius: "12px",
                    marginTop: "8px",
                    backgroundColor: "#ffffff"
                  }}
                >
                  {cities.filter(c => c.toLowerCase().includes(selectedCity.toLowerCase())).map((city, idx) => (
                    <li
                      key={idx}
                      className="list-group-item list-group-item-action text-dark border-0 border-bottom"
                      style={{ cursor: "pointer", backgroundColor: "#ffffff" }}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent onBlur from firing before click
                        setSelectedCity(city);
                        setShowSuggestions(false);
                      }}
                    >
                      {city}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <select
              className="form-select shadow-sm bg-white text-dark"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              style={{ minWidth: "150px", borderRadius: "12px", border: "1px solid #e2e8f0" }}
            >
              <option value="DESC">Highest Clicks</option>
              <option value="ASC">Lowest Clicks</option>
            </select>

            <div className="d-flex align-items-center gap-2">
              <input
                type="date"
                className="form-control shadow-sm bg-white text-dark"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
              />
              <span className="text-muted fw-bold">to</span>
              <input
                type="date"
                className="form-control shadow-sm bg-white text-dark"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
              />
            </div>

            <span className="badge bg-white text-dark shadow-sm px-2 py-2 rounded-pill fs-6 border">
              Total Restaurants: <span className="text-primary fw-bold">{restaurantClicks.length}</span>
            </span>
          </div>
        </div>

        {loading ? (
          <div className="text-center my-5 py-5">
            <FaSpinner className="spinner fs-1 text-primary mb-3" spin />
            <h5 className="text-muted">Crunching the data...</h5>
          </div>
        ) : sortedRestaurantClicks.length === 0 ? (
          <div className="text-center my-5 py-5 bg-white rounded-4 shadow-sm border border-light">
            <img src="https://cdn-icons-png.flaticon.com/512/7486/7486747.png" alt="No data" width="120" className="opacity-50 mb-4" />
            <h4 className="text-muted fw-bold">No Engagement Data Yet</h4>
            <p className="text-muted">When users click on restaurants, their activity will appear here.</p>
          </div>
        ) : (
          <div className="row g-4">
            {sortedRestaurantClicks.map((item, index) => (
              <div className="col-lg-4 col-md-6" key={index}>
                <div className="restaurant-card h-100 d-flex flex-column p-4 border border-light">
                  <div className="card-header-icon">
                    <FaUtensils />
                  </div>
                  <div className="text-center flex-grow-1">
                    <h5 className="fw-bold mb-1" style={{ color: "#334155" }}>{item.name}</h5>
                    <p className="text-primary fw-semibold small mb-3">
                      <i className="bi bi-geo-alt-fill me-1"></i> {item.city_name}
                    </p>
                    <p className="text-muted small mb-4">Total Engagement</p>

                    <div className="mb-4">
                      <div className="click-badge">
                        {item.total_clicks}
                      </div>
                      <div className="text-muted fw-semibold small mt-1">TOTAL CLICKS</div>
                    </div>
                  </div>

                  <div className="mt-auto pt-3 border-top">
                    <button
                      className="btn btn-premium w-100 d-flex align-items-center justify-content-center gap-2"
                      onClick={() => handleViewClick(item)}
                    >
                      <FaEye /> View History
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Clicks Modal */}
      <div
        className="modal fade"
        id="viewClicksModal"
        tabIndex="-1"
        aria-labelledby="viewClicksModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
            <div className="modal-header modal-custom-header px-4 py-3">
              <div>
                <h5 className="modal-title fw-bold mb-0" id="viewClicksModalLabel">
                  Engagement History
                </h5>
                <small className="text-white-50">{selectedRestaurant?.name}</small>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body p-4 bg-light">
              {selectedRestaurant?.views?.length > 0 ? (
                <ul className="list-group list-group-custom">
                  {selectedRestaurant.views.map((date, idx) => (
                    <li className="list-group-item d-flex justify-content-between align-items-center px-4 py-3" key={idx}>
                      <div className="d-flex align-items-center gap-3">
                        <div className="bg-primary bg-opacity-10 text-primary rounded-circle p-2 d-flex align-items-center justify-content-center">
                          <FaCalendarAlt />
                        </div>
                        <span className="fw-semibold text-secondary">Click #{selectedRestaurant.views.length - idx}</span>
                      </div>
                      <span className="badge bg-white text-dark shadow-sm px-3 py-2 border rounded-pill">{date}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-5">
                  <h6 className="text-muted mb-0">No historical timestamp data available.</h6>
                </div>
              )}
            </div>
            <div className="modal-footer bg-white border-top px-4 py-3">
              <button
                type="button"
                className="btn btn-light fw-bold px-4"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantClicks;
